// apps/provider/src/services/api/services.js
// Service (appointment type) admin management methods
//
// Methods: getServices, createService, updateService, getStripeProducts, importStripeProduct
//
// Response types: @prism-ehr/shared → AppointmentType

/**
 * @returns {Object} Services domain methods (bound to `this` of the composed apiService)
 */
export function createServicesMethods() {
  return {
    /**
     * Get all services for the authenticated user's organization.
     *
     * Xano: GET /services
     * Xano file: 859_services_GET.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType[]>}
     */
    getServices() {
      return this.request('/services', { method: 'GET' });
    },

    /**
     * Create a new service (appointment type) with Stripe product.
     *
     * Xano: POST /services
     * Xano file: 862_services_POST.xs
     *
     * @param {Object} data - Service fields
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType>}
     */
    createService(data) {
      return this.request('/services', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update an existing service (appointment type).
     * Only calls Stripe update_price when default_price changes.
     *
     * Xano: PATCH /services/{id}
     * Xano file: 864_services_appointment_type_id_PATCH.xs
     *
     * @param {string} id - Appointment type ID
     * @param {Object} updates - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType>}
     */
    updateService(id, updates) {
      return this.request(`/services/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
    },

    /**
     * Get Stripe products available for import.
     *
     * Xano: GET /services/stripe-products
     * Xano file: 860_services_stripe_products_GET.xs
     *
     * @returns {Promise<Object[]>} List of Stripe product objects
     */
    getStripeProducts() {
      return this.request('/services/stripe-products', { method: 'GET' });
    },

    /**
     * Import a Stripe product as a service.
     *
     * Xano: POST /services/import
     *
     * @param {Object} data - Import data
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType>}
     */
    importStripeProduct(data) {
      return this.request('/services/import', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Reorder services by setting sort_order.
     *
     * Xano: POST /services-reorder
     * Xano file: services-reorder_POST.xs
     *
     * @param {string[]} serviceIds - Ordered array of appointment type IDs
     * @returns {Promise<import('@prism-ehr/shared').AppointmentType[]>}
     */
    reorderServices(serviceIds) {
      return this.request('/services-reorder', {
        method: 'POST',
        body: JSON.stringify({ service_ids: serviceIds }),
      });
    },
  };
}
